<template>
  <b-card class="!border-[1px] !border-[#E2E2E2] !border-solid !shadow-none !-mt-[10px]">
    <h3 class="font-semibold text-[#333] text-[20px] pb-[16px] mb-0">
      Edit Campaign
    </h3>
    <Step :current-step="currentStep" />

    <validation-observer
      ref="formRules"
      #default="{ errors }"
    >
      <b-form
        ref="formRef"
        @submit.prevent
      >
        <div
          v-show="currentStep === 0"
          class="p-[14px] bg-[#FCFCFC] my-[16px] rounded-[10px] flex flex-col gap-[24px]"
        >
          <collapsible
            v-for="item in fields"
            :key="item.key"
            :title="item.label"
            :icon="item.icon"
            :visible="sectionStates[item.key]"
            :has-error="getSectionErrorCount(item.key, errors) > 0"
            :error-count="getSectionErrorCount(item.key, errors)"
            @update:visible="val => $set(sectionStates, item.key, val)"
          >
            <template v-for="input in item.inputs">
              <!-- Group type inputs -->
              <div
                v-if="input.type === 'group'"
                :key="input.key || (input.children && input.children[0] && input.children[0].key)"
                class="flex gap-[16px]"
              >
                <validated-input
                  v-for="child in input.children"
                  :key="child.key"
                  v-model="form[child.key]"
                  :item="child"
                  class="flex-1"
                />
              </div>

              <validated-input
                v-else
                :key="input.key"
                v-model="form[input.key]"
                :item="input"
              />
            </template>
          </collapsible>
        </div>

        <div
          v-show="currentStep !== 0"
          class="py-[24px]"
        >
          <template v-if="hasVisitedStep1">
            <div class="flex flex-wrap mb-[30px] gap-[10px] items-center text-[#08A0F7] border-[1px] border-[#08A0F7] py-[10px] px-[14px] rounded-[8px] bg-[#DFF3FF]">
              <img
                src="https://storage.googleapis.com/komerce/assets/icons/star-1-filled-blue.svg"
                alt="preview-campaign"
                width="24px"
                height="24px"
              >
              <span>Berikut preview campaign kamu dan rekomendasi creator yang paling relevan berdasarkan kriteria yang diisi.</span>
            </div>
            <preview
              :form="form"
            />
            <creator-recomendation
              :form="form"
            />
          </template>
        </div>

        <div class="flex justify-end flex-wrap items-center gap-[12px] px-[24px] -mx-[21px] py-[16px] border-t-[1px] border-[#E2E2E2]">
          <b-button
            v-if="currentStep > 0"
            variant="outline-primary"
            class="min-w-[120px] h-[40px] rounded-[6px] !border-[#F95031] !text-[#F95031] hover:bg-[#FFEBE7] font-semibold"
            @click="prevStep"
          >
            Kembali
          </b-button>
          <b-button
            v-else
            variant="outline-secondary"
            class="min-w-[120px] h-[40px] rounded-[6px] !border-[#E2E2E2] !text-[#626262] hover:bg-[#F4F4F4] font-semibold"
            :disabled="loadingSubmit"
            @click="$router.back()"
          >
            Batal
          </b-button>

          <b-button
            v-if="currentStep === 0"
            variant="primary"
            class="min-w-[120px] h-[40px] rounded-[6px] !bg-[#F95031] !border-[#F95031] hover:!bg-[#E04427] font-semibold text-white"
            :disabled="loadingDefaultValue || failedLoadDefaultValue || !isEditable"
            @click="nextStep"
          >
            Selanjutnya
          </b-button>
          <template
            v-if="currentStep === 1"
          >
            <b-button
              variant="primary"
              class="min-w-[120px] h-[40px] rounded-[6px] !bg-[#F95031] !border-[#F95031] hover:!bg-[#E04427] font-semibold text-white gap-[8px] !flex items-center justify-center"
              :disabled="loadingSubmit || !isEditable"
              @click="submitCampaign"
            >
              Simpan Perubahan
              <b-spinner
                v-if="loadingSubmit"
                small
              />
            </b-button>
          </template>
        </div>
      </b-form>
    </validation-observer>
  </b-card>
</template>

<script>
import { ValidationObserver } from 'vee-validate'
import { alertSuccess } from '@toast'
import { YEAR_MONTH_DAY } from '@/libs/filterDate'
import Step from '../create-campaign/Step.vue'
import { CREATE_CAMPAIGN_FORM_FIELDS, CAMPAIGN_FORM_TAP, CAMPAIGN_FORM_MCN } from '../config'
import Collapsible from '../create-campaign/Collapsible.vue'
import ValidatedInput from '../create-campaign/ValidatedInput.vue'
import Preview from '../create-campaign/Preview.vue'
import CreatorRecomendation from '../create-campaign/CreatorRecomendation.vue'
import { getSectionErrorCount, handleValidationErrors } from '../composables/useCampaignValidation'

export default {
  components: {
    Step,
    Collapsible,
    ValidatedInput,
    ValidationObserver,
    Preview,
    CreatorRecomendation,
  },
  data() {
    return {
      currentStep: 0,
      hasVisitedStep1: false,
      form: {
        product_type: 'Free',
      },
      sectionStates: {
        campaign_info: true,
        scope_of_works: false,
        agreements: false,
      },
      alertSuccess,
      CREATE_CAMPAIGN_FORM_FIELDS,
      loadingSubmit: false,
      loadingDefaultValue: true,
      failedLoadDefaultValue: false,
      initialChannel: '',
      formStatus: '',
    }
  },
  computed: {
    id() {
      return this.$route.params.id
    },
    isEditable() {
      return this.formStatus === 'pre_campaign' || this.formStatus === 'draft'
    },
    aggreementField() {
      const channel = String(this.form.channel || '').toLowerCase()
      const isMcnOnly = channel.includes('mcn') && !channel.includes('tap')
      return {
        label: 'Agreements',
        key: 'agreements',
        icon: 'k-document-text',
        inputs: isMcnOnly ? CAMPAIGN_FORM_MCN : CAMPAIGN_FORM_TAP,
      }
    },
    fields() {
      const baseFields = CREATE_CAMPAIGN_FORM_FIELDS.map(section => ({
        ...section,
        inputs: section.inputs.map(input => {
          if (input.type !== 'group') return input
          return {
            ...input,
            children: input.children.map(child => {
              if (child.key !== 'end_date') return child
              return {
                ...child,
                disabledDates: this.form.start_date
                  ? { to: new Date(this.form.start_date) }
                  : undefined,
              }
            }),
          }
        }),
      }))

      return [...baseFields, this.aggreementField]
    },
  },
  watch: {
    currentStep(val) {
      if (val >= 1) this.hasVisitedStep1 = true
    },
  },
  created() {
    this.fetchData()
  },
  methods: {
    /**
     * Mengambil detail campaign dari API menggunakan ID dari route.
     * Memetakan data respons ke dalam objek form, menangani field wajib
     * dan opsional (termasuk komisi dan tipe konten) secara dinamis.
     *
     * @async
     * @returns {Promise<void>}
     */
    async fetchData() {
      this.loadingDefaultValue = true
      this.failedLoadDefaultValue = false
      try {
        if (!this.id) return
        const { data } = await this.$http_new.get(`/komclass/api/v1/komlabs/campaigns/public/${this.id}`)
        if (data && data.data) {
          const detail = data.data
          this.initialChannel = detail.channel || ''
          this.formStatus = data.data.status

          if (!this.isEditable) {
            this.$toast_error({ message: 'Campaign hanya dapat diedit saat berstatus Pre-Campaign.' })
            this.$router.replace({
              name: 'campaign-detail-komlabs',
              params: { id: this.id },
              query: { tab: this.formStatus === 'ongoing' ? 'ongoing' : undefined },
            })
            return
          }
          let campaignType = []
          if (Array.isArray(detail.campaign_type)) {
            campaignType = detail.campaign_type
          } else if (detail.campaign_type) {
            campaignType = [detail.campaign_type]
          }

          let productType = 'Free'
          if (detail.product_type) {
            productType = Array.isArray(detail.product_type) ? detail.product_type[0] : detail.product_type
          }

          let productCategory = []
          if (Array.isArray(detail.product_category)) {
            productCategory = detail.product_category
          } else if (detail.product_category) {
            productCategory = [detail.product_category]
          }

          const mappedForm = {
            name: detail.name,
            campaign_type: campaignType,
            product_type: productType,
            product_category: productCategory,
            total_creators: detail.total_creators,
            content_amount: detail.content_amount,
            start_date: detail.start_date,
            end_date: detail.end_date,
            thumbnail: detail.thumbnail_url || detail.thumbnail,
          }

          /**
           * Daftar field opsional yang akan di-map secara dinamis ke form
           * jika nilai tersebut tersedia dan tidak null di respons API.
           * @type {string[]}
           */
          const optionalFields = [
            'description', 'product_link', 'campaign_source', 'channel',
            'min_followers', 'min_gmv', 'content_brief',
            'commission_tap', 'commission_mcn', 'addon_reimburse',
          ]

          optionalFields.forEach(field => {
            if (detail[field] !== undefined && detail[field] !== null) {
              mappedForm[field] = detail[field]
            }
          })

          if (detail.addon_reimburse) {
            mappedForm.addon_reimburse = detail.addon_reimburse
            if (Array.isArray(mappedForm.campaign_type) && !mappedForm.campaign_type.includes('Add-on')) {
              mappedForm.campaign_type.push('Add-on')
            }
          }

          if (detail.content_type) {
            const rawContent = Array.isArray(detail.content_type) ? detail.content_type[0] : detail.content_type
            if (typeof rawContent === 'string' && rawContent) {
              const normalized = rawContent.charAt(0).toUpperCase() + rawContent.slice(1).toLowerCase()
              mappedForm.content_type = (normalized === 'Video' || normalized === 'Live') ? normalized : rawContent
            } else {
              mappedForm.content_type = rawContent
            }
          }

          if (detail.domicile) {
            mappedForm.domicile = Array.isArray(detail.domicile) ? detail.domicile : [detail.domicile]
          }

          this.form = {
            ...this.form,
            ...mappedForm,
          }
        }
      } catch (err) {
        this.failedLoadDefaultValue = true
        this.$toast_error({ message: 'Gagal mengambil data campaign, refresh halaman untuk memulai ulang' })
      } finally {
        this.loadingDefaultValue = false
      }
    },
    getSectionErrorCount(sectionKey, errors) {
      return getSectionErrorCount(sectionKey, errors)
    },
    handleValidationErrors() {
      handleValidationErrors({
        sectionStates: this.sectionStates,
        errors: this.$refs.formRules?.errors,
      })
    },
    async nextStep() {
      if (this.currentStep > 0) return

      const isValid = await this.$refs.formRules.validate()

      if (!isValid) {
        this.handleValidationErrors()
        return
      }

      this.currentStep = 1
    },
    prevStep() {
      if (this.currentStep > 0) {
        this.currentStep = 0
      }
    },
    async submitCampaign() {
      this.loadingSubmit = true
      try {
        const isValid = await this.$refs.formRules.validate()

        if (!isValid) {
          this.loadingSubmit = false
          this.currentStep = 0
          this.handleValidationErrors()
          return
        }

        if (!this.isEditable) {
          this.loadingSubmit = false
          this.$toast_error({ message: 'Campaign hanya dapat diedit saat berstatus Pre-Campaign.' })
          this.$router.replace({
            name: 'campaign-detail-komlabs',
            params: { id: this.id },
          })
          return
        }

        const url = this.formStatus === 'draft'
          ? `/komclass/api/v1/komlabs/campaigns/${this.id}/draft`
          : `/komclass/api/v1/komlabs/campaigns/${this.id}/pre-campaign`

        const formData = new FormData()
        Object.entries(this.form).forEach(([key, value]) => {
          if (value === null || value === undefined || key === 'status' || key === 'addon_reimburse') return

          if (key === 'thumbnail') {
            const file = Array.isArray(value) ? value[0] : value
            if (file instanceof File || file instanceof Blob) {
              formData.append('thumbnail', file)
            } else if (typeof file === 'string' && file) {
              formData.append('thumbnail', file)
              formData.append('thumbnail_url', file)
            }
            return
          }

          if (key === 'start_date' || key === 'end_date') {
            if (value) formData.append(key, YEAR_MONTH_DAY(value))
            return
          }

          if (key === 'campaign_type') {
            if (Array.isArray(value)) {
              value.forEach(item => formData.append('campaign_type', item))
            } else if (value) {
              formData.append('campaign_type', value)
            }
            return
          }

          if (key === 'content_type') {
            const formatContentType = val => {
              if (typeof val === 'string' && val) {
                const norm = val.charAt(0).toUpperCase() + val.slice(1).toLowerCase()
                return (norm === 'Video' || norm === 'Live') ? norm : val
              }
              return val
            }
            if (Array.isArray(value)) {
              value.forEach(item => formData.append('content_type', formatContentType(item)))
            } else if (value) {
              formData.append('content_type', formatContentType(value))
            }
            return
          }

          if (key === 'product_type') {
            if (Array.isArray(value)) {
              value.forEach(item => formData.append('product_type', item))
            } else if (value) {
              formData.append('product_type', value)
            }
            return
          }

          if (key === 'product_category') {
            if (Array.isArray(value)) {
              value.forEach(item => formData.append('product_category', item))
            } else if (value) {
              formData.append('product_category', value)
            }
            return
          }

          if (key === 'domicile') {
            if (Array.isArray(value)) {
              formData.append('domicile', value.join(', '))
            } else if (value) {
              formData.append('domicile', value)
            }
            return
          }

          if (key === 'commission_tap') {
            const channel = String(this.form.channel || '').toLowerCase()
            const isMcnOnly = channel.includes('mcn') && !channel.includes('tap')
            if (!isMcnOnly && value !== '') {
              formData.append('commission_tap', value)
            }
            return
          }

          if (Array.isArray(value)) {
            value.forEach(item => formData.append(key, item))
          } else {
            formData.append(key, value)
          }
        })

        const hasAddon = (Array.isArray(this.form.campaign_type) && this.form.campaign_type.includes('Add-on'))
          || this.form.addon_reimburse === true
          || this.form.addon_reimburse === 'true'
          || this.form.addon_reimburse === 1
        formData.append('addon_reimburse', hasAddon ? 'true' : 'false')

        if (this.formStatus === 'draft') {
          formData.append('status', 'pre_campaign')
        }

        await this.$http_new.put(url, formData)

        this.$toast_success({
          title: 'Campaign berhasil diperbarui',
          message: 'Campaign Anda sudah berhasil diperbarui',
        })

        this.$router.push({
          name: 'campaign-detail-komlabs',
          params: { id: this.id },
          query: { tab: 'pre campaign' },
        })
      } catch (err) {
        this.$toast_error({
          title: 'Gagal memperbarui campaign',
          message: err?.response?.data?.message || 'Terjadi kesalahan. Silakan coba lagi.',
        })
      } finally {
        this.loadingSubmit = false
      }
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
